/**
 * How the Create Invoice form reads a failed `POST /v2/invoices/`.
 *
 * Two outcomes matter. A 422 naming `account_payload` means the brokerage
 * has never been invoiced and the form retries with the Account half of the
 * body. Everything else stops the flow and is shown to the user as the
 * backend worded it, with the form and the draft left untouched so the user
 * can correct and generate again.
 *
 * Every 409 belongs to the second group, and is decided by status alone: the
 * `detail` strings are display copy and may change. Besides the concurrency
 * ones (an existing invoice with different details, a draft modified
 * concurrently), the backend now refuses to move an Ascend Account between
 * brokerages ("linked to another brokerage", "already linked to a different
 * Ascend Account") and reports an invoice number collision whose retry
 * succeeds. The form never sends `link_account_id` itself.
 */

import { ApiError } from "../../../api";

/** The one recoverable failure: the brokerage has no Ascend Account yet. The
 *  word boundary keeps this from matching the other 422, "account_user_payload
 *  is required...", and the status keeps any 409 wording out of it. */
export function needsAccountPayload(e: unknown): boolean {
    return e instanceof ApiError && e.status === 422 && /\baccount_payload\b/i.test(e.message ?? "");
}

/** What the "Your invoice wasn't generated" alert says. A 409 always carries
 *  the backend's `detail`; `ApiError` already prefers it for its message. */
export function generateFailureMessage(e: unknown): string {
    if (e instanceof ApiError && e.status === 409) return e.detail ?? e.message;
    return e instanceof Error ? e.message : "Unknown error";
}
